import { AwsS3MultipartOptions, AwsS3Part } from '@uppy/aws-s3';
import { UppyFile } from '@uppy/core';

import { APIBaseChronos } from '../../../api/hosts';

export enum UploadType {
  Raw = 'Raw',
  Zip = 'zip',
}

export type GenericUppyFile = UppyFile<{}, {}>;

interface UploadPartNumberRequest {
  uploadId: string;
  key: string;
  partNumber: number;
  body: unknown;
  signal?: AbortSignal;
}

interface CompleteMultipartUploadRequest {
  uploadId: string;
  key: string;
  parts: AwsS3Part[];
}

interface AbortMultipartUploadRequest {
  uploadId?: string | undefined;
  key: string;
}

interface ListPartsRequest {
  uploadId?: string | undefined;
  key: string;
}

interface PresignedPostResponse {
  url: string;
  fields: Record<string, string>;
  expiresIn: number;
}

interface CachedPresignedPost {
  data: PresignedPostResponse;
  expiry: number;
}

export function getUppyAWSS3Options(
  getAccessToken: () => Promise<string | undefined>,
  caseId: string,
  type: UploadType,
): AwsS3MultipartOptions<{}, {}> {
  let cachedPresignedPost: CachedPresignedPost | undefined = undefined;

  return {
    limit: 6,

    shouldUseMultipart(file: GenericUppyFile) {
      return (file?.size ?? 0) > 100 * 2 ** 20;
    },

    getUploadParameters: async () => {
      // Use cached details if we're >1 minute away from expiry
      if (cachedPresignedPost && cachedPresignedPost.expiry - Date.now() > 60 * 1000) {
        return cachedPresignedPost.data;
      }

      const accessToken = await getAccessToken();

      const response = await fetch(`${APIBaseChronos}/api/storage/presigned-post?caseId=${caseId}&type=${type}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error('Fetching presigned post failed');
      }

      const res = await response.json();

      cachedPresignedPost = {
        data: res,
        expiry: Date.now() + 10000,
      };

      return res;
    },

    createMultipartUpload: async (file: GenericUppyFile) => {
      const accessToken = await getAccessToken();

      const response = await fetch(`${APIBaseChronos}/api/storage/multipart/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          caseId,
          filename: file.name,
          type: type === UploadType.Raw ? 'raw' : 'zip',
        }),
      });

      const data = await response.json();

      return {
        uploadId: data.UploadId,
        key: data.Key,
      };
    },

    signPart: async (_: GenericUppyFile, partData: UploadPartNumberRequest) => {
      const accessToken = await getAccessToken();

      const response = await fetch(`${APIBaseChronos}/api/storage/multipart/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(partData),
      });

      const data = await response.json();

      return {
        url: data.url,
      };
    },

    completeMultipartUpload: async (_: GenericUppyFile, body: CompleteMultipartUploadRequest) => {
      const accessToken = await getAccessToken();

      const response = await fetch(`${APIBaseChronos}/api/storage/multipart/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      return response.json();
    },

    abortMultipartUpload: async (_: GenericUppyFile, { uploadId, key }: AbortMultipartUploadRequest) => {
      const accessToken = await getAccessToken();

      const response = await fetch(`${APIBaseChronos}/api/storage/multipart/abort`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          uploadId,
          key,
          caseId,
        }),
      });

      return await response.json();
    },

    listParts: async (_: GenericUppyFile, { uploadId, key }: ListPartsRequest): Promise<AwsS3Part[]> => {
      const accessToken = await getAccessToken();

      try {
        const response = await fetch(`${APIBaseChronos}/api/storage/multipart/list-parts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            uploadId,
            key,
            caseId,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to list multipart upload parts: ${response.statusText}`);
        }

        const data = await response.json();

        return data.Parts.map((part: { PartNumber: number; ETag: string }) => ({
          PartNumber: part.PartNumber,
          ETag: part.ETag,
        }));
      } catch (error) {
        console.error('Error listing multipart upload parts:', error);
        return [];
      }
    },
  };
}
